import { defineStore } from 'pinia';
import StorageHandler from '@/utils/StorageHandler.js';
import Translator from '@/utils/Translator.js';
import FetchData from "@/utils/FetchData.js";

export const useLanguageStore = defineStore('languageStore', {
  state: () => ({
    selectedLanguage: null,
    translations: {}, // Store translations here
    languages: []
  }),
  actions: {
    async setLanguageFromUser(userLang) {
      // Find the language object that matches the user's lang code
      const userLanguage = this.languages.find(lang => lang.code === userLang);
      
      if (userLanguage) {
        // Set the user's preferred language
        await this.setLanguage(userLanguage);
      }
    },
    async LoadLanguages(){
      const storedLanguages = StorageHandler.LoadSessionStorage('languages');
      if (storedLanguages) {
        this.languages = storedLanguages;
        return;
      }
      const route = "LoadLanguages";
      const config = {
        select: [
          {
            "name": "code",
            "value": "Code"
          },
          {
            "name": "name",
            "value": "Name"
          },
          {
            "name": "flag",
            "value": "Flag"
          },
          "alternative"
        ],
        where: [
          "Active eq 1"
        ]
      };
      try{
        const response = await FetchData(config, route, "POST", true);
        if(response[0]?.code) {
          StorageHandler.InsertSessionStorage('languages', response);
        }
        this.languages = response[0]?.code ? response : [];
      } catch (error) {
        console.error("Error loading languages:", error);
      }
    },
    async loadLanguage(userLang = null) {
      const storedLanguage = StorageHandler.LoadSessionStorage('selectedLanguage');
      
      let languageToUse;
      
      if (userLang) {
        languageToUse = this.languages.find(lang => lang.code === userLang);
      }
      
      this.selectedLanguage = languageToUse || storedLanguage || {
        code: 'cs',
        name: 'Czech',
        flag: 'https://upload.wikimedia.org/wikipedia/commons/c/cb/Flag_of_the_Czech_Republic.svg',
      };

      // Only load translations if we don't have them or language changed
      if (!this.translations || Object.keys(this.translations).length === 0) {
        const translator = new Translator();
        this.translations = await translator.LoadTranslates();
      }
    },

    async setLanguage(language) {
      // Only reload translations if language actually changed
      const languageChanged = this.selectedLanguage?.code !== language.code;
      
      this.selectedLanguage = language;
      StorageHandler.InsertSessionStorage('selectedLanguage', language);
      StorageHandler.RemoveSessionStorageAll(["user", "selectedLanguage"]);

      if (languageChanged) {
        const translator = new Translator();
        this.translations = await translator.LoadTranslates();
      }
    },
    getSelectedLanguage() {
      return this.selectedLanguage;
    },
    getTranslation(key) {
      // Check if key exists in translations
      if (!key || key === '') return key;
      
      let exists = false;
      if (this.translations) {
        exists = Object.keys(this.translations).includes(key);
      }
      
      if (exists) {
        return this.translations[key] || key;
      } else {
        // Don't fetch individual translations - they should be loaded in bulk
        // Just return the key and log missing translation
        if (process.env.NODE_ENV === 'development') {
          console.warn(`Translation missing for key: ${key}`);
        }
        
        // Queue for batch addition instead of individual fetch
        this.queueMissingTranslation(key);
        return key;
      }
    },

    // Add this new method
    queueMissingTranslation(key) {
      if (!this._missingTranslations) {
        this._missingTranslations = new Set();
      }
      
      if (!this._missingTranslations.has(key)) {
        this._missingTranslations.add(key);
        
        // Debounce batch addition
        clearTimeout(this._addTranslationsTimeout);
        this._addTranslationsTimeout = setTimeout(async () => {
          const translator = new Translator();
          for (const missingKey of this._missingTranslations) {
            await translator.AddTranslation(missingKey);
          }
          this._missingTranslations.clear();
        }, 1000);
      }
    },
    async ReloadTranslations() {
      const translator = new Translator();
      this.translations = await translator.ReloadTranslations(); // Reload translations
    }
  },
});